/** Pointy-top hexagon vertices in normalized objectBoundingBox space (0–1). */
const POINTY_TOP_HEX_VERTICES: ReadonlyArray<readonly [number, number]> = [
	[0.5, 0],
	[1, 0.25],
	[1, 0.75],
	[0.5, 1],
	[0, 0.75],
	[0, 0.25],
] as const;

function pointyTopHexVerticesPx(
	width: number,
	height: number,
): Array<[number, number]> {
	return [
		[width / 2, 0],
		[width, height * 0.25],
		[width, height * 0.75],
		[width / 2, height],
		[0, height * 0.75],
		[0, height * 0.25],
	];
}

export function buildRoundedPolygonPath(
	vertices: ReadonlyArray<readonly [number, number]>,
	cornerRadius: number,
): string {
	const n = vertices.length;
	const radius = Math.max(0, cornerRadius);
	if (radius === 0) {
		const flat = vertices
			.map(([x, y], index) => `${index === 0 ? "M" : "L"} ${x} ${y}`)
			.join(" ");
		return `${flat} Z`;
	}

	let path = "";
	for (let index = 0; index < n; index += 1) {
		const previous = vertices[(index - 1 + n) % n];
		const current = vertices[index];
		const next = vertices[(index + 1) % n];

		const edgeInX = current[0] - previous[0];
		const edgeInY = current[1] - previous[1];
		const edgeOutX = next[0] - current[0];
		const edgeOutY = next[1] - current[1];
		const edgeInLength = Math.hypot(edgeInX, edgeInY);
		const edgeOutLength = Math.hypot(edgeOutX, edgeOutY);
		if (edgeInLength === 0 || edgeOutLength === 0) {
			continue;
		}

		const cornerRadius = Math.min(
			radius,
			edgeInLength / 2,
			edgeOutLength / 2,
		);
		const startX = current[0] - (edgeInX / edgeInLength) * cornerRadius;
		const startY = current[1] - (edgeInY / edgeInLength) * cornerRadius;
		const endX = current[0] + (edgeOutX / edgeOutLength) * cornerRadius;
		const endY = current[1] + (edgeOutY / edgeOutLength) * cornerRadius;

		path +=
			index === 0 ? `M ${startX} ${startY}` : ` L ${startX} ${startY}`;
		path += ` Q ${current[0]} ${current[1]} ${endX} ${endY}`;
	}
	return `${path} Z`;
}

/**
 * Builds a closed SVG path for a pointy-top hexagon with rounded corners.
 * Coordinates are normalized for `clipPathUnits="objectBoundingBox"`.
 */
export function buildRoundedPointyHexagonPath(cornerRadius: number): string {
	return buildRoundedPolygonPath(POINTY_TOP_HEX_VERTICES, cornerRadius);
}

/** Pixel-space path for reliable CSS `clip-path: path(...)`. */
export function buildRoundedPointyHexagonPathPx(
	width: number,
	height: number,
	cornerRadiusPx: number,
): string {
	return buildRoundedPolygonPath(
		pointyTopHexVerticesPx(width, height),
		cornerRadiusPx,
	);
}

function toCssPathClip(pathData: string): string {
	const escaped = pathData.replace(/"/g, '\\"');
	return `path("${escaped}")`;
}

export interface HexagonPathTileCornerRadii {
	/** Outer corner radius in px. */
	outerPx: number;
	/** Inner corner radius in px (outer minus border band). */
	innerPx: number;
}

/** Same relationship as {@link GlassBorder} inner radius: outer minus border band. */
export function resolveHexagonPathTileCornerRadiiPx(params: {
	outerCornerRadiusPx: number;
	borderWidthPx: number;
}): HexagonPathTileCornerRadii {
	const outerPx = Math.max(0, params.outerCornerRadiusPx);
	const innerPx = Math.max(0, outerPx - Math.max(0, params.borderWidthPx));
	return { outerPx, innerPx };
}

export interface HexagonPathTileClipPaths {
	outerPath: string;
	innerPath: string;
	outerClipPath: string;
	innerClipPath: string;
}

export function buildHexagonPathTileClipPaths(params: {
	outerCornerRadiusPx: number;
	borderWidthPx: number;
	hexagonWidth: number;
	hexagonHeight: number;
}): HexagonPathTileClipPaths {
	const { outerPx, innerPx } = resolveHexagonPathTileCornerRadiiPx(params);
	const width = Math.max(1, params.hexagonWidth);
	const height = Math.max(1, params.hexagonHeight);
	const outerPath = buildRoundedPointyHexagonPathPx(width, height, outerPx);
	const innerPath = buildRoundedPointyHexagonPathPx(width, height, innerPx);
	return {
		outerPath,
		innerPath,
		outerClipPath: toCssPathClip(outerPath),
		innerClipPath: toCssPathClip(innerPath),
	};
}
